/**
 * MERGE POLISHES PAGE (Admin Tool)
 *
 * Single-polish merge tool. Renames every user item that maps to the
 * "from" polish so it points at the "to" polish, plus cascades through
 * every other collection that references the polish by name or slug —
 * polishConfirms migration, creations' itemsUsed[] rewriting, and
 * itemsUsedRefs[] recomputation. All shared with the bulk cleanup page
 * (PolishNamesCleanup) via `renamePolish()` — see that helper for
 * cascade details.
 *
 * REQUIRES: isAdmin: true on the user's Firestore profile.
 *
 * Mirrors the structure of MergeBrands.tsx — same auth gate, same
 * admin-tools UI conventions.
 *
 * Brand item rows under brands/{brandId}/items are NOT touched here;
 * verified-brand product records stay aligned via the trigger when their
 * own brand/name are edited through the brand-management UI.
 */

import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getDoc, doc } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { ArrowLeft } from 'lucide-react';
import { polishSlug } from '../utils/polishSlug';
import { renamePolish } from '../services/polishRename';

export default function MergePolishes() {
  // Pre-fill from URL params (e.g. when launched from the Admin Dashboard's
  // "Catalog merge candidates" list).
  const [searchParams] = useSearchParams();
  const [fromBrand, setFromBrand] = useState(searchParams.get('fromBrand') ?? '');
  const [fromName, setFromName] = useState(searchParams.get('fromName') ?? '');
  const [toBrand, setToBrand] = useState('');
  const [toName, setToName] = useState('');
  const [status, setStatus] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<string[]>([]);
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user?.uid || null);
      if (user) {
        const snap = await getDoc(doc(db, 'users', user.uid));
        setIsAdmin(snap.exists() && snap.data()?.isAdmin === true);
      } else {
        setIsAdmin(false);
      }
      setCheckingAdmin(false);
    });
    return () => unsub();
  }, []);

  const log = (m: string) => {
    setResults((prev) => [...prev, m]);
    console.log(m);
  };

  const merge = async () => {
    if (!fromBrand.trim() || !fromName.trim() || !toBrand.trim() || !toName.trim()) {
      setStatus('Fill in both polishes first.');
      return;
    }

    const fromSlug = polishSlug(fromBrand, fromName);
    const toSlug = polishSlug(toBrand, toName);

    if (fromSlug === toSlug) {
      setStatus('"From" and "to" resolve to the same slug — nothing to merge.');
      return;
    }

    setIsRunning(true);
    setStatus('Renaming…');
    setResults([]);

    try {
      const summary = await renamePolish({
        fromBrand,
        fromName,
        toBrand,
        toName,
        onLog: log,
      });
      log(
        `Done: items ${summary.itemsUpdated}/${summary.itemsMatched}, confirms ${summary.confirmsMigrated}/${summary.confirmsScanned}, creations ${summary.creationsUpdated}/${summary.creationsScanned}${summary.catalogDocDeleted ? ', catalog doc deleted' : ''}.`
      );
      setStatus(
        `✅ Merge complete. Triggers will refresh the destination catalog doc shortly.`
      );
    } catch (err: any) {
      console.error(err);
      setStatus(`❌ Merge failed: ${err?.message ?? err}`);
    } finally {
      setIsRunning(false);
    }
  };

  if (checkingAdmin) {
    return <div className="max-w-3xl mx-auto p-8 text-glass-secondary">Checking access…</div>;
  }
  if (!currentUser) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-glass-secondary">
        Please sign in to use this admin tool.
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-glass-secondary">
        This page is admin-only.
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      <Link to="/admin" className="inline-flex items-center gap-1 text-glass-muted hover:text-glass mb-4">
        <ArrowLeft className="w-4 h-4" /> Admin
      </Link>

      <h1 className="text-2xl font-bold text-glass mb-2">Merge Polishes</h1>
      <p className="text-glass-secondary text-sm mb-6">
        Rewrites every user item matching the "from" polish so it points at
        the "to" polish (brand + name + slug), and deletes the abandoned
        catalog doc. The catalog triggers rebuild the destination doc
        automatically.
      </p>

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div className="bg-slate-800/40 border border-slate-600 rounded-lg p-4">
          <h2 className="text-glass font-medium mb-3">From (duplicate)</h2>
          <label className="block text-xs text-glass-muted mb-1">Brand</label>
          <input
            type="text"
            value={fromBrand}
            onChange={(e) => setFromBrand(e.target.value)}
            disabled={isRunning}
            className="w-full mb-3 bg-slate-900 border border-slate-600 rounded px-3 py-2 text-glass"
          />
          <label className="block text-xs text-glass-muted mb-1">Polish name</label>
          <input
            type="text"
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            disabled={isRunning}
            className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-glass"
          />
          {fromBrand && fromName && (
            <div className="text-xs text-glass-muted mt-2">
              slug: <code>{polishSlug(fromBrand, fromName)}</code>
            </div>
          )}
        </div>

        <div className="bg-slate-800/40 border border-slate-600 rounded-lg p-4">
          <h2 className="text-glass font-medium mb-3">To (canonical)</h2>
          <label className="block text-xs text-glass-muted mb-1">Brand</label>
          <input
            type="text"
            value={toBrand}
            onChange={(e) => setToBrand(e.target.value)}
            disabled={isRunning}
            className="w-full mb-3 bg-slate-900 border border-slate-600 rounded px-3 py-2 text-glass"
          />
          <label className="block text-xs text-glass-muted mb-1">Polish name</label>
          <input
            type="text"
            value={toName}
            onChange={(e) => setToName(e.target.value)}
            disabled={isRunning}
            className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-glass"
          />
          {toBrand && toName && (
            <div className="text-xs text-glass-muted mt-2">
              slug: <code>{polishSlug(toBrand, toName)}</code>
            </div>
          )}
        </div>
      </div>

      <button
        onClick={merge}
        disabled={isRunning}
        className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
      >
        {isRunning ? 'Merging…' : 'Merge'}
      </button>

      {status && <p className="mt-4 text-glass">{status}</p>}

      {results.length > 0 && (
        <div className="mt-4 bg-slate-900 border border-slate-700 rounded-lg p-4">
          <h3 className="text-glass-secondary text-sm mb-2">Log</h3>
          <pre className="text-xs text-glass-muted whitespace-pre-wrap">
            {results.join('\n')}
          </pre>
        </div>
      )}
    </div>
  );
}